import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ActionModule } from '@typesApp/ActionModule'
import { HostEnvironment, HostSession } from '@typesApp/Environment'
import {
  connectorActionModuleSelector,
  connectorEnvironmentSelector,
  connectorInitActionModule,
  connectorInitAnonymousSession,
  connectorInitEnvironment,
  connectorInitGuestSession,
  connectorInitSession,
  connectorSessionSelector,
  loggingOutSelector,
  sessionExpiredSelector,
} from '@redux/slices/connectorSlice'
import {
  initGlobalLocale,
  normalizeEnvironment,
  sessionValid,
  isEnvironmentChanged,
  isSessionChanged,
  canRequestAuthToken,
  isAnonymousSession,
  isGuestSessionWithUserId,
} from '@utils/appInitUtil'
import { useSession } from '@hooks/useSession'

type UseAppInitParams = {
  session: HostSession
  environment: HostEnvironment
  actionModule: ActionModule
}

export const useAppInit = (params: UseAppInitParams) => {
  const { session, environment, actionModule } = params
  const dispatch = useDispatch()
  const { initContext, initSession } = useSession()

  const isLoggingOut = useSelector(loggingOutSelector)
  const isSessionExpired = useSelector(sessionExpiredSelector)

  const currentSession = useSelector(connectorSessionSelector)
  const currentEnvironment = useSelector(connectorEnvironmentSelector)
  const currentActionModule = useSelector(connectorActionModuleSelector)

  useEffect(() => {
    const environmentWithPath = normalizeEnvironment(environment)
    if (environmentWithPath && (!currentEnvironment || isEnvironmentChanged(currentEnvironment, environmentWithPath))) {
      dispatch(connectorInitEnvironment(environmentWithPath))
      initGlobalLocale(environmentWithPath)
    }
  }, [dispatch, environment, currentEnvironment])

  useEffect(() => {
    if (actionModule && !currentActionModule) {
      dispatch(connectorInitActionModule(actionModule))
    }
  }, [dispatch, actionModule, currentActionModule])

  useEffect(() => {
    const isAnonymous = isAnonymousSession(session)
    const isGuestSession = isGuestSessionWithUserId(session)
    const isSessionValid = sessionValid({ isLoggingOut, isSessionExpired, session })

    if (isAnonymous) {
      dispatch(connectorInitAnonymousSession(session))
      return
    }

    // for guest session, commerce F/E may send the userId only
    if (isGuestSession) {
      dispatch(connectorInitGuestSession(session))
    }

    if (
      isSessionValid &&
      canRequestAuthToken({ session, actionModule }) &&
      (!currentSession || isSessionChanged(currentSession, session))
    ) {
      dispatch(connectorInitSession(session))

      // get latest session id from B/E to avoid fetching auth token
      // with stale session id from commerce F/E
      initContext().then(context => {
        let sessionCopy = session

        // no session id if token has expired or not logged in
        if (context && session.sessionId !== context.sessionId) {
          if (context.sessionId) {
            console.info(
              'directbilling - using session id %s from server (received from commerce FE was %s)',
              context.sessionId,
              session.sessionId
            )
          }

          sessionCopy = {
            ...session,
            sessionId: context.sessionId,
            isPartiallyAuthenticated: context.isPartiallyAuthenticated,
          }
        }

        if (isSessionChanged(sessionCopy, session)) {
          dispatch(connectorInitSession(sessionCopy))
        }

        if (context?.sessionId && canRequestAuthToken({ session: sessionCopy, actionModule })) {
          initSession(sessionCopy)
        }
      })
    }
  }, [dispatch, session, actionModule, isLoggingOut, isSessionExpired])
}
